import { TextField, Typography } from '@material-ui/core'
import ApartmentOutlinedIcon from '@material-ui/icons/ApartmentOutlined'
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useEffect, useMemo, useState } from 'react'

import { getTranslate, lang } from '../../localization'
import { DestinationOption } from '../../types/accommodationsSubscribedToChannel'
import { SearchAvailabilityInput } from '../../types/searchAvailability'
import useStyles from './styles/index'

export interface DestinationAutoCompleteProps {
  options: DestinationOption[]
  setSearchAvailabilityInput: React.Dispatch<
    React.SetStateAction<SearchAvailabilityInput>
  >
  searchAvailabilityInput: SearchAvailabilityInput
  loadingForWBSData: boolean
  setBookingRedirect: React.Dispatch<React.SetStateAction<string>>
}

export const DestinationAutoComplete: React.FC<
  DestinationAutoCompleteProps
> = ({
  options,
  setSearchAvailabilityInput,
  searchAvailabilityInput,
  loadingForWBSData,
  setBookingRedirect
}) => {
  const classes = useStyles({ lang })
  const [inputValue, setInputValue] = useState('')

  // 🔥 FORCE SELECTION EVERY RENDER - NO useState needed!
  const selectedOption = useMemo(() => {
    if (!options.length || !searchAvailabilityInput) return undefined
    return options.find((item) => {
      if (searchAvailabilityInput.cityID) {
        return item.cityID === searchAvailabilityInput.cityID
      } else if (searchAvailabilityInput.propertyCode) {
        return item.destinationCode === searchAvailabilityInput.propertyCode
      }
    })
  }, [
    options,
    searchAvailabilityInput.cityID,
    searchAvailabilityInput.propertyCode
  ])

  useEffect(() => {
    if (selectedOption && selectedOption.destinationName) {
      setInputValue(selectedOption.destinationName)
    }
  }, [selectedOption])

  return (
    <div className={classes.root}>
      <Autocomplete
        disableClearable
        disabled={options.length === 1}
        options={options}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)} // 👈 this line enables typing
        getOptionSelected={(option, value) => {
          if (!value) return false

          if (option.isCity && value.isCity) {
            return option.cityID === value.cityID
          }

          if (!option.isCity && !value.isCity) {
            return option.destinationCode === value.destinationCode
          }

          return false
        }}
        filterOptions={(opts, { inputValue: searchText }) => {
          if (!searchText) return opts

          const search = searchText.toLowerCase().trim()
          const words = search.split(/\s+/)

          return opts.filter((item) => {
            const hotelName = item.destinationName?.toLowerCase() || ''
            const cityName = item.cityOfAccommodation?.toLowerCase() || ''

            return words.every(
              (w) => hotelName.includes(w) || cityName.includes(w)
            )
          })
        }}
        onChange={(_, newValue) => {
          if (newValue) {
            if (newValue?.isCity) {
              setSearchAvailabilityInput((prev) => ({
                ...prev,
                cityID: newValue.cityID,
                propertyCode: undefined
              }))
            } else if (newValue.destinationCode) {
              setSearchAvailabilityInput((prev) => ({
                ...prev,
                propertyCode: newValue.destinationCode,
                providerCodes: [newValue.providerCode],
                cityID: undefined
              }))
              setBookingRedirect(newValue.bookingRedirect)
            }
          } else {
            setSearchAvailabilityInput((prev) => ({
              ...prev,
              propertyCode: undefined,
              providerCodes: [],
              cityID: undefined
            }))
          }
        }}
        renderOption={(option) => (
          <div
            className={classes.optionContainer}
            dir={lang === 'fa' ? 'rtl' : 'ltr'}>
            {option.isCity ? (
              <LocationOnOutlinedIcon className={classes.icon} />
            ) : (
              <ApartmentOutlinedIcon className={classes.icon} />
            )}
            <Typography variant="caption">{option.destinationName}</Typography>
          </div>
        )}
        groupBy={(option) =>
          option.isCity ? getTranslate('City') : getTranslate('Hotel')
        }
        getOptionLabel={(option) => option.destinationName}
        size="small"
        inputMode="search"
        classes={{
          endAdornment: classes.noEndAdornment,
          option: classes.rootTextfield,
          groupLabel: classes.groupLabel
        }}
        loading={loadingForWBSData}
        loadingText={getTranslate('Loading') + '...'}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getTranslate('Where is your destination?')}
            variant="outlined"
            className={classes.placeHolder}
            InputProps={{
              ...params.InputProps,
              classes: { notchedOutline: classes.noBorder }
            }}
          />
        )}
      />
    </div>
  )
}
